import React from 'react';
import './contactinformation.scss';
import Tooltip from '../common/tooltip/tooltip';
import {
    validateContactInfo,
    MAX_LEN,
    isUS,
    isCA
} from '../../utils/contactvalidation';

type ContactInfo = {
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    stateCode: string;
    zipCode: string;
    country: string;
};

interface ContactInformationProps {
    contactInfo: ContactInfo;
    setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
    submitAttempted?: boolean;
}

const STATE_OPTIONS = [
    { code: 'AL', label: 'Alabama' }, { code: 'AK', label: 'Alaska' }, { code: 'AS', label: 'American Samoa' },
    { code: 'AZ', label: 'Arizona' }, { code: 'AR', label: 'Arkansas' }, { code: 'AA', label: 'Armed Forces - Americas' },
    { code: 'AE', label: 'Armed Forces - Europe/Africa/Canada' }, { code: 'AP', label: 'Armed Forces - Pacific' },
    { code: 'CA', label: 'California' }, { code: 'CO', label: 'Colorado' }, { code: 'CT', label: 'Connecticut' },
    { code: 'DE', label: 'Delaware' }, { code: 'DC', label: 'District of Columbia' }, { code: 'FM', label: 'Federated States of Micronesia' },
    { code: 'FL', label: 'Florida' }, { code: 'GA', label: 'Georgia' }, { code: 'GU', label: 'Guam' },
    { code: 'HI', label: 'Hawaii' }, { code: 'ID', label: 'Idaho' }, { code: 'IL', label: 'Illinois' },
    { code: 'IN', label: 'Indiana' }, { code: 'IA', label: 'Iowa' }, { code: 'KS', label: 'Kansas' },
    { code: 'KY', label: 'Kentucky' }, { code: 'LA', label: 'Louisiana' }, { code: 'MA', label: 'Massachusetts' },
    { code: 'MD', label: 'Maryland' }, { code: 'ME', label: 'Maine' }, { code: 'MI', label: 'Michigan' },
    { code: 'MN', label: 'Minnesota' }, { code: 'MO', label: 'Missouri' }, { code: 'MP', label: 'Northern Mariana Islands' },
    { code: 'MS', label: 'Mississippi' }, { code: 'MT', label: 'Montana' }, { code: 'NC', label: 'North Carolina' },
    { code: 'ND', label: 'North Dakota' }, { code: 'NE', label: 'Nebraska' }, { code: 'NH', label: 'New Hampshire' },
    { code: 'NJ', label: 'New Jersey' }, { code: 'NM', label: 'New Mexico' }, { code: 'NV', label: 'Nevada' },
    { code: 'NY', label: 'New York' }, { code: 'OH', label: 'Ohio' }, { code: 'OK', label: 'Oklahoma' },
    { code: 'OR', label: 'Oregon' }, { code: 'PA', label: 'Pennsylvania' }, { code: 'PR', label: 'Puerto Rico' },
    { code: 'PW', label: 'Palau' }, { code: 'RI', label: 'Rhode Island' }, { code: 'SC', label: 'South Carolina' },
    { code: 'SD', label: 'South Dakota' }, { code: 'TN', label: 'Tennessee' }, { code: 'TX', label: 'Texas' },
    { code: 'UT', label: 'Utah' }, { code: 'VA', label: 'Virginia' }, { code: 'VI', label: 'Virgin Islands' },
    { code: 'VT', label: 'Vermont' }, { code: 'WA', label: 'Washington' }, { code: 'WI', label: 'Wisconsin' },
    { code: 'WV', label: 'West Virginia' }, { code: 'WY', label: 'Wyoming' },
] as const;

const STATE_LOOKUP = new Map(
    STATE_OPTIONS.flatMap(opt => [
        [opt.code.toLowerCase(), opt.code],
        [opt.label.toLowerCase(), opt.code]
    ])
);

const getStateLabelByCode = (code: string) =>
    STATE_OPTIONS.find(o => o.code === code)?.label || code;

const collapseWhitespace = (s: string) => s.replace(/\s+/g, ' ').trim();

const ContactInformation: React.FC<ContactInformationProps> = ({ contactInfo, setContactInfo, submitAttempted }) => {
    const [touched, setTouched] = React.useState<Record<string, boolean>>({});

    const refs = {
        streetAddress1: React.useRef<HTMLInputElement>(null),
        streetAddress2: React.useRef<HTMLInputElement>(null),
        city: React.useRef<HTMLInputElement>(null),
        state: React.useRef<HTMLSelectElement>(null),
        zipCode: React.useRef<HTMLInputElement>(null),
    };

    const onFieldChange =
        <K extends keyof ContactInfo>(field: K) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                const value = e.target.value;
                if (field === 'state') {
                    const code = value;
                    const label = getStateLabelByCode(code);
                    setContactInfo(prev => ({ ...prev, state: label, stateCode: code }));
                    return;
                }
                setContactInfo(prev => ({ ...prev, [field]: value }));
            };

    const handleBlur =
        (field: keyof ContactInfo) =>
            () => {
                setTouched(prev => ({ ...prev, [field]: true }));
                if (field === 'streetAddress1' || field === 'streetAddress2' || field === 'city') {
                    setContactInfo(prev => ({ ...prev, [field]: collapseWhitespace((prev[field] as string) || '') }));
                }
                if (field === 'zipCode') {
                    setContactInfo(prev => {
                        let v = (prev.zipCode || '').trim();
                        if (isCA(prev.country)) {
                            v = v.toUpperCase().replaceAll(/\s+/g, '');
                            if (v.length === 6) v = `${v.slice(0, 3)} ${v.slice(3)}`;
                        }
                        return { ...prev, zipCode: v };
                    });
                }
            };

    const resolveStateValue = (input: string) => {
        if (!input) return '';
        const resolved = STATE_LOOKUP.get(input.toLowerCase());
        if (!resolved) console.warn(`Invalid state value: "${input}"`);
        return resolved || '';
    };

    const stateSelectValue = React.useMemo(
        () => contactInfo.stateCode || resolveStateValue(contactInfo.state),
        [contactInfo.stateCode, contactInfo.state]
    );

    const errors = React.useMemo(() => validateContactInfo(contactInfo), [contactInfo]);


    const showError = (field: keyof ContactInfo) => Boolean((touched[field] || submitAttempted) && errors[field]);
    const errId = (id: string) => `${id}-error`;

    React.useEffect(() => {
        if (!submitAttempted) return;
        const order: (keyof ContactInfo)[] = ['streetAddress1', 'streetAddress2', 'city', 'state', 'zipCode'];
        const first = order.find(f => errors[f]);
        const el =
            first === 'state' ? refs.state.current
                : first === 'streetAddress1' ? refs.streetAddress1.current
                    : first === 'streetAddress2' ? refs.streetAddress2.current
                        : first === 'city' ? refs.city.current
                            : first === 'zipCode' ? refs.zipCode.current
                                : null;
        if (el) {
            try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { }
            el.focus();
        }
    }, [submitAttempted, errors]);

    return (
        <>
            <h2 className="contact-info-heading" aria-label="Contact Information">Contact Information</h2>
            <div className="contact-information-container" data-testid="contact-information-container">
                <div className="contact-info-row" data-testid="contact-info-address-row1">
                    <div className="contact-info-field">
                        <label htmlFor="streetAddress1">
                            {' '}Street Address 1
                            <Tooltip message="Required for postal communication." placement="top" />
                        </label>
                        <input
                            id="streetAddress1"
                            ref={refs.streetAddress1}
                            type="text"
                            placeholder="Street Address 1"
                            value={contactInfo.streetAddress1}
                            onChange={onFieldChange('streetAddress1')}
                            onBlur={handleBlur('streetAddress1')}
                            maxLength={MAX_LEN}
                            aria-invalid={showError('streetAddress1')}
                            aria-describedby={showError('streetAddress1') ? errId('streetAddress1') : undefined}
                            aria-errormessage={showError('streetAddress1') ? errId('streetAddress1') : undefined}
                            required
                            className={showError('streetAddress1') ? 'input-error' : undefined}
                        />
                        {showError('streetAddress1') && (
                            <div id={errId('streetAddress1')} role="alert" aria-live="polite" className="error-text">
                                {errors.streetAddress1}
                            </div>
                        )}
                    </div>

                    <div className="contact-info-field">
                        <label htmlFor="streetAddress2">Street Address 2</label>
                        <input
                            id="streetAddress2"
                            ref={refs.streetAddress2}
                            type="text"
                            placeholder="Street Address 2"
                            value={contactInfo.streetAddress2}
                            onChange={onFieldChange('streetAddress2')}
                            onBlur={handleBlur('streetAddress2')}
                            maxLength={MAX_LEN}
                            aria-invalid={showError('streetAddress2')}
                            aria-describedby={showError('streetAddress2') ? errId('streetAddress2') : undefined}
                            aria-errormessage={showError('streetAddress2') ? errId('streetAddress2') : undefined}
                            className={showError('streetAddress2') ? 'input-error' : undefined}
                        />
                        <span className="helper-text"><strong>Optional</strong></span>
                        {showError('streetAddress2') && (
                            <div id={errId('streetAddress2')} role="alert" aria-live="polite" className="error-text">
                                {errors.streetAddress2}
                            </div>
                        )}
                    </div>

                    <div className="contact-info-field">
                        <label htmlFor="city">City</label>
                        <input
                            id="city"
                            ref={refs.city}
                            type="text"
                            placeholder="City"
                            value={contactInfo.city}
                            onChange={onFieldChange('city')}
                            onBlur={handleBlur('city')}
                            maxLength={MAX_LEN}
                            aria-invalid={showError('city')}
                            aria-describedby={showError('city') ? errId('city') : undefined}
                            aria-errormessage={showError('city') ? errId('city') : undefined}
                            required
                            className={showError('city') ? 'input-error' : undefined}
                        />
                        {showError('city') && (
                            <div id={errId('city')} role="alert" aria-live="polite" className="error-text">
                                {errors.city}
                            </div>
                        )}
                    </div>
                </div>

                <div className="contact-info-row" data-testid="contact-info-address-row2">
                    <div className="contact-info-field state-field">
                        <label htmlFor="state">State, Province, or Region</label>
                        <select
                            id="state"
                            ref={refs.state}
                            value={stateSelectValue}
                            onChange={onFieldChange('state')}
                            onBlur={handleBlur('state')}
                            aria-label="State, Province, or Region"
                            aria-invalid={showError('state')}
                            aria-describedby={showError('state') ? errId('state') : undefined}
                            aria-errormessage={showError('state') ? errId('state') : undefined}
                            required
                            className={showError('state') ? 'input-error' : undefined}
                        >
                            <option value="">Select a State, Province, or Region…</option>
                            {STATE_OPTIONS.map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {showError('state') && (
                            <div id={errId('state')} role="alert" aria-live="polite" className="error-text">
                                {errors.state}
                            </div>
                        )}
                    </div>

                    <div className="contact-info-field">
                        <label htmlFor="zipCode">Zip or Postal Code</label>
                        <input
                            id="zipCode"
                            ref={refs.zipCode}
                            type="text"
                            placeholder="Zip or Postal Code"
                            value={contactInfo.zipCode}
                            onChange={onFieldChange('zipCode')}
                            onBlur={handleBlur('zipCode')}
                            inputMode={isUS(contactInfo.country) ? 'numeric' : 'text'}
                            pattern={
                                isUS(contactInfo.country)
                                    ? '\\d{5}(-\\d{4})?'
                                    : isCA(contactInfo.country)
                                        ? '[A-Za-z][0-9][A-Za-z] [0-9][A-Za-z][0-9]'
                                        : undefined
                            }
                            maxLength={isUS(contactInfo.country) ? 10 : isCA(contactInfo.country) ? 7 : 32}
                            aria-invalid={showError('zipCode')}
                            aria-describedby={showError('zipCode') ? errId('zipCode') : undefined}
                            aria-errormessage={showError('zipCode') ? errId('zipCode') : undefined}
                            required={isUS(contactInfo.country) || isCA(contactInfo.country)}
                            className={showError('zipCode') ? 'input-error' : undefined}
                        />
                        {showError('zipCode') && (
                            <div id={errId('zipCode')} role="alert" aria-live="polite" className="error-text">
                                {errors.zipCode}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactInformation;